//@ts-ignore
import { logout } from "@gik/auth/utils/auth";
import { useAppStore } from "@gik/core/store/AppStore";
import { useEnvStore } from "@gik/core/store/EnvStore";
import { useUserStore } from "@gik/core/store/UserStore";
import { storage } from "@gik/core/utils/Storage";
import {
	appUpdateStartPolling,
	appUpdateStopPolling,
	checkAppVersion,
} from "@gik/core/utils/appUpdates";
import { useBemCN } from "@gik/core/utils/bemBlock";
import { useDialogStore } from "@gik/ui-legacy/Dialogs";
import { StickyHorizontal } from "@gik/ui-legacy/Sticky";
import { UI } from "@gik/ui-legacy/UIManager";
import { AppHeaderSticky } from "@gik/ui-legacy/gik/AppHeader/AppHeaderSticky";
import { CorpTopNav } from "@gik/ui-legacy/gik/AppHeader/CorpTopNav";
import { TestUnbounceBar } from "@gik/ui-legacy/gik/AppHeader/CorpTopNav/TestUnbounceBar";
import dynamic from "next/dynamic";
import { Router, useRouter } from "next/router";
import NProgress from "nprogress";
import React from "react";
import { AppFooter } from "../AppFooter";
import { useQueryStringHandler } from "../QueryStringHandler/QueryStringHandler";

const AppImports = dynamic(
	() => import("./AppImports").then((mod) => mod.AppImports),
	{
		ssr: false,
	},
);

const warnUnsavedChanges = false;

interface AppLayoutProps
	extends React.PropsWithChildren<IPageLayoutAttachment> {
	hideHeader?: boolean;
	hideFooter?: boolean;
	hasAnimations?: boolean;
	groupSlug?: string;
	className?: string;
	headerClassName?: string;
	footerClassName?: string;
}

export interface IPageLayoutAttachment {
	navBarAttachment?: React.ReactNode;
	footerAttachment?: React.ReactNode;
}

const blockName = "app-layout";

function AppLayoutComp({
	children,
	navBarAttachment,
	footerAttachment,
	hideHeader = false,
	hideFooter = false,
	className,
	headerClassName,
	footerClassName,
}: AppLayoutProps) {
	const bem = useBemCN(blockName);

	useAppRouterPersonalizations();
	useQueryStringHandler();

	return (
		<>
			<AppImports />
			<div {...bem(null, null, className)}>
				<TestUnbounceBar />
				<CorpTopNav />
				{!hideHeader && (
					<AppHeaderSticky
						navBarAttachment={navBarAttachment}
						className={headerClassName}
					/>
				)}
				<main>{children}</main>
				{!hideFooter && (
					<StickyHorizontal className={footerClassName}>
						{footerAttachment}
						<AppFooter />
					</StickyHorizontal>
				)}
			</div>
		</>
	);
}

export const AppLayout = AppLayoutComp;

function useAppRouterPersonalizations() {
	const router = useRouter();

	const routeChangeCompleteFn = React.useCallback(() => {
		if (useUserStore.getState().shouldCloseModalsAfterNavigation) {
			// close all dialogs after a successful route navigation
			// use a timeout of 1 second to allow the new page to load first
			UI.closeAllDialogs();
		} else {
			// set shouldCloseModalsAfterNavigation to true again
			useUserStore.getState().setShouldCloseModalsAfterNavigation(true);
		}

		// log the user out after a navigation if shouldLogout is true
		if (useUserStore.getState().shouldLogout) {
			logout();
			useUserStore.getState().setShouldLogout(false);
		}

		if (useUserStore.getState().shouldUnblurAfterNavigation) {
			// unblurApp();
		} else {
			// set shouldUnblurAfterNavigation to true again
			useUserStore.getState().setShouldUnblurAfterNavigation(true);
		}
	}, []);

	const routeChangeStartHandler = React.useCallback(
		(url, { shallow }) => {
			if (!router) return;
			const currentPath = router.asPath;

			// prevent navigation if nextjs is trying to navigate to the same route without shallow routing
			if (currentPath === url && !shallow) {
				NProgress.done();
				// NOTE: an error has to be thrown here and it can't be caught (we need to let the error through to nextjs)
				// this will result in a unhandled promise rejection error log being thrown in the console which can be ignored.
				throw new Error(
					`Route navigation aborted from ${currentPath} to ${url}`,
				);
			}
		},
		[router],
	);

	React.useEffect(() => {
		Router.events.on("routeChangeStart", routeChangeStartHandler);
		return () => {
			Router.events.off("routeChangeStart", routeChangeStartHandler);
		};
	}, [routeChangeStartHandler]);

	React.useEffect(() => {
		// start polling for app updates
		if (useEnvStore.getState().APP_UPDATE_POLLING) {
			appUpdateStartPolling();
			// give the app a couple of seconds to boot and then perform an initial check
			setTimeout(() => {
				checkAppVersion();
			}, 3000);
		}

		window.onunload = () => {
			// store opened modals
			const dialogs = useDialogStore.getState().dialogs;
			if (dialogs.length && useAppStore.getState().isUpdating) {
				const dialogIds = dialogs
					.map((item) => item.key)
					.filter((item) => item !== null);
				storage.set("open-dialogs", JSON.stringify(dialogIds));
			}
		};

		// register app level unbefore unload event
		window.onbeforeunload = () => {
			if (!warnUnsavedChanges) return undefined;

			const dirtyForms = useAppStore.getState().dirtyForms;
			// skip this if a form is being submitted or none of the forms are dirty
			if (useAppStore.getState().isSubmitting || !dirtyForms.length) {
				return undefined;
			}

			// NOTE: You can't set custom message anymore in most modern browsers.
			// For consistency we we just use the same message as modern browser would use.
			return "Changes you made may not be saved.";
		};

		setTimeout(() => {
			storage.remove("open-dialogs");
		}, 5000);

		Router.events.on("routeChangeComplete", routeChangeCompleteFn);
		return () => {
			Router.events.off("routeChangeComplete", routeChangeCompleteFn);
			// stop polling for app updates
			if (useEnvStore.getState().APP_UPDATE_POLLING) appUpdateStopPolling();
		};
	}, [routeChangeCompleteFn]);
}
